/**
 * The single source of truth for CONTENT languages — the language exam
 * content (passages/questions/exams) is written in. Deliberately separate
 * from the *interface* locale list (`src/i18n/routing.ts`), which still
 * includes Russian (`ru`) as a UI locale — that is an account-level
 * preference (`profiles.uiLocale`) unrelated to which languages have exam
 * programs, so it is NOT affected by this list.
 *
 * Only en/tr are offered for now — Turkish CEFR and English IELTS/CEFR are
 * the two exam programs actually being built. Russian was removed here
 * (2026-09) once there was no Russian exam content to justify listing it as
 * a filter option; add it back (with its flag) once Russian exam content
 * ships. Uzbek is a UI locale but has no exam program content at all yet.
 *
 * Previously this exact array was copy-pasted into three admin dialogs
 * (exam-form-dialog, passage-form-dialog, question-form-dialog), which is
 * exactly the kind of drift risk the app's own repository pattern exists to
 * avoid elsewhere — consolidated here so every consumer (admin content
 * forms, the student-facing mock-language filter) reads one list.
 */
export const CONTENT_LANGUAGES = [
  { code: "en", label: "English", flagSrc: "/images/flags/gb.svg" },
  { code: "tr", label: "Türkçe", flagSrc: "/images/flags/tr.svg" },
] as const;

export type ContentLanguageCode = (typeof CONTENT_LANGUAGES)[number]["code"];

export const DEFAULT_CONTENT_LANGUAGE: ContentLanguageCode = "en";

export function isContentLanguageCode(value: string | null | undefined): value is ContentLanguageCode {
  return CONTENT_LANGUAGES.some((lang) => lang.code === value);
}

export function contentLanguageLabel(code: string): string {
  return CONTENT_LANGUAGES.find((lang) => lang.code === code)?.label ?? code.toUpperCase();
}

/** Flag image path for a content language, e.g. for the exam list's language
 * badge or the mock-language switcher — see `public/images/flags/README.md`
 * for why these are real SVGs rather than flag emoji (Windows renders those
 * as plain "GB"/"TR" text, not pictures). Returns null for a code with no
 * flag mapped (e.g. old Russian-language exams, if any still exist in the
 * database) so callers can fall back to a plain label instead of a broken
 * image. */
export function contentLanguageFlagSrc(code: string): string | null {
  return CONTENT_LANGUAGES.find((lang) => lang.code === code)?.flagSrc ?? null;
}

/** Cookie the student-facing mock-language switcher reads/writes (see
 * `src/components/shared/mock-language-switcher.tsx`). A cookie rather than
 * a `profiles` column: this is a lightweight, purely client-side filter
 * preference, not account data — keeping it out of the database avoids a
 * schema migration for what is essentially a UI filter default. */
export const CONTENT_LANGUAGE_COOKIE = "examora_mock_language";

/** Fired on `window` whenever the cookie changes, so any mounted page (the
 * exams list) can react without prop-drilling state up through the shared
 * header/layout that renders the switcher. */
export const CONTENT_LANGUAGE_CHANGE_EVENT = "examora:mock-language-changed";
